import { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, Send, Inbox, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import io from 'socket.io-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WA_SERVICE_URL = process.env.REACT_APP_WA_SERVICE_URL;

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalMessages: 0,
    sentMessages: 0,
    receivedMessages: 0,
    connected: false
  });
  const [qrCode, setQrCode] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchStats();
    checkWhatsAppStatus();

    // Connect to WebSocket
    const newSocket = io(WA_SERVICE_URL);
    setSocket(newSocket);

    // Join user-specific room
    newSocket.emit('join', { userId: user.id });

    newSocket.on('connection', (data) => {
      setConnectionStatus(data.status);
      if (data.qr) {
        setQrCode(data.qr);
      }
    });

    newSocket.on('qr', (data) => {
      setQrCode(data.qr);
      setConnectionStatus('qr');
    });

    newSocket.on('message', () => {
      fetchStats();
    });

    const interval = setInterval(() => {
      fetchStats();
      if (connectionStatus === "disconnected") {
        checkWhatsAppStatus();
      }
    }, 20000);

    return () => {
      // console.log("Mulai Disconnect")
      clearInterval(interval);
      if (newSocket) newSocket.disconnect();
    };
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkWhatsAppStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectionStatus(response.data.status);
      if (response.data.qr) {
        setQrCode(response.data.qr);
      } else if (response.data.status === 'connected') {
        setQrCode(null);
      }
    } catch (error) {
      console.error('Error checking WhatsApp status:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/whatsapp/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Berhasil logout dari WhatsApp');
      setQrCode(null);
      setConnectionStatus('disconnected');
      setTimeout(() => checkWhatsAppStatus(), 2000);
    } catch (error) {
      toast.error('Gagal logout dari WhatsApp');
      console.error('Error logging out:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-600 mt-2">Monitor aktivitas WhatsApp Anda</p>
        </div>
        <Button
          data-testid="refresh-button"
          onClick={() => {
            fetchStats();
            checkWhatsAppStatus();
          }}
          className="bg-green-600 hover:bg-green-700"
        >
          <RefreshCw className="mr-2" size={18} />
          Refresh
        </Button>
      </div>

      {/* Connection Status Card */}
      <Card className="glass-effect card-hover">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              {connectionStatus === 'connected' ? (
                <>
                  <Wifi className="text-green-600" size={24} />
                  <span className="text-green-600">Terhubung</span>
                </>
              ) : (
                <>
                  <WifiOff className="text-red-600" size={24} />
                  <span className="text-red-600">Tidak Terhubung</span>
                </>
              )}
            </span>
            {connectionStatus === 'connected' && (
              <Button
                data-testid="logout-wa-button"
                onClick={handleLogout}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
              >
                Logout WA
              </Button>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {qrCode && connectionStatus === 'qr' ? (
            <div className="flex flex-col items-center">
              <div className="qr-container">
                <img src={qrCode} alt="QR Code" className="w-64 h-64" data-testid="qr-code" />
              </div>
              <p className="mt-4 text-center text-gray-600">
                Scan QR code ini dengan WhatsApp Anda untuk terhubung
              </p>
            </div>
          ) : connectionStatus === 'connected' ? (
            <p className="text-center text-gray-600">WhatsApp Anda sudah terhubung dan siap digunakan</p>
          ) : (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
              </div>
              <p className="text-gray-600">Menghubungkan ke WhatsApp...</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="stat-card" data-testid="total-messages-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Total Pesan</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stats.totalMessages}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <MessageCircle className="text-blue-600" size={28} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="sent-messages-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Pesan Terkirim</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stats.sentMessages}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Send className="text-green-600" size={28} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="received-messages-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Pesan Masuk</p>
                <p className="text-3xl font-bold text-gray-800 mt-2">{stats.receivedMessages}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Inbox className="text-purple-600" size={28} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
